import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, Crown, Home, Sparkles, ListOrdered, Map, HelpCircle, MessageSquare } from 'lucide-react';
import { company } from '../data/content';

export function FinalCta() {
  return (
    <section className="py-20 md:py-28 gradient-hero relative overflow-hidden">
      <div className="absolute inset-0 dotted-bg opacity-20" aria-hidden />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <Crown className="w-12 h-12 text-[#E0A72C] mx-auto mb-4 drop-shadow" />
        <div className="text-white/85 uppercase tracking-[0.2em] text-sm font-semibold">Bereit für den</div>
        <h2 className="mt-3 text-white font-extrabold text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
          <span className="gold-text">Haushelden</span>-Effekt?
        </h2>
        <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto">
          Kostenlose Beratung. Schnelle Termine. Faire Einschätzung.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href="#kontakt" className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-[15px]">
            Beratung sichern <ArrowRight className="w-4 h-4" />
          </a>
          <a href={`tel:${company.phoneRaw}`} className="btn-ghost-light inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-[15px] font-medium">
            <Phone className="w-4 h-4" /> {company.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#081533] text-white/80 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="Die Haushelden" className="h-16 w-auto" />
              <div className="leading-tight">
                <div className="text-white font-bold text-lg">Die Haushelden</div>
                <div className="text-[#E0A72C] text-[11px] uppercase tracking-[0.18em] font-semibold">Sanierung · Reinigung · Pflege</div>
              </div>
            </div>
            <p className="mt-5 max-w-md leading-relaxed text-white/70">
              Ihre zuverlässigen Helden für Reinigung, Sanierung und Pflege rund um Ihre Immobilie – vom Pflaster bis zum Dach.
            </p>
            <div className="mt-6 flex flex-col gap-2 text-sm">
              <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 hover:text-white">
                <Mail className="w-4 h-4 text-[#E0A72C]" /> {company.email}
              </a>
              <a href={`tel:${company.phoneRaw}`} className="inline-flex items-center gap-2 hover:text-white">
                <Phone className="w-4 h-4 text-[#E0A72C]" /> {company.phone}
              </a>
              <div className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#E0A72C]" /> {company.address.street}, {company.address.zip} {company.address.city}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">Navigation</h4>
            <ul className="mt-4 space-y-2.5 text-sm" data-testid="footer-nav">
              {[
                { l: 'Start', h: '/#start', I: Home },
                { l: 'Leistungen', h: '/#leistungen', I: Sparkles },
                { l: 'Prozess', h: '/#prozess', I: ListOrdered },
                { l: 'Einsatzgebiet', h: '/#einsatzgebiet', I: Map },
                { l: 'FAQ', h: '/#faq', I: HelpCircle },
                { l: 'Kontakt', h: '/#kontakt', I: MessageSquare },
              ].map(({ l, h, I }) => (
                <li key={h}>
                  <a href={h} className="group inline-flex items-center gap-2.5 hover:text-white transition-colors">
                    <span className="w-7 h-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-[#E0A72C] group-hover:bg-[#E0A72C] group-hover:text-[#0B1E44] transition-colors">
                      <I className="w-3.5 h-3.5" />
                    </span>
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">Öffnungszeiten</h4>
            <ul className="mt-4 space-y-1.5 text-sm">
              {company.hours.map((h, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="text-white/70">{h.d}</span>
                  <span>{h.t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-white/50">
              Einsatzgebiet: Hamburg, Lübeck, Rethwisch, Bad Oldesloe, Hannover, Bremen, Ingolstadt, München, Nürnberg, Regensburg, Augsburg – sowie deutschlandweit nach Absprache.
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Die Haushelden. Alle Rechte vorbehalten.</div>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-white" data-testid="footer-impressum-link">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white" data-testid="footer-datenschutz-link">Datenschutz</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
